import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Dot,
  type TooltipProps,
} from 'recharts';
import type { TrajectoryPoint } from '../../types/predictions';
import { BASIC_CATEGORIES, type BasicId } from '../../types/predictions';

interface TrajectoryChartProps {
  data: TrajectoryPoint[];
  threshold: number;
  basicId: string;
  currentPercentile: number | null;
}

/**
 * TrajectoryChart Component
 * Displays a line chart showing BASIC score projection over time
 *
 * Features:
 * - Line chart with projected percentiles
 * - Threshold reference line
 * - Current score marker
 * - Interactive tooltips with details
 * - Responsive sizing
 */
export const TrajectoryChart: React.FC<TrajectoryChartProps> = ({
  data,
  threshold,
  basicId,
  currentPercentile,
}) => {
  const basicName = BASIC_CATEGORIES[basicId as BasicId]?.name || basicId;

  // Format data for Recharts (add month 0 for current score)
  const chartData = [
    {
      month: 0,
      date: 'Today',
      percentile: currentPercentile,
      status: currentPercentile !== null && currentPercentile < threshold * 0.5 ? 'EXCELLENT' :
              currentPercentile !== null && currentPercentile < threshold ? 'SAFE' :
              currentPercentile !== null && currentPercentile < threshold * 1.1 ? 'WARNING' : 'CRITICAL',
      remainingViolations: null,
    },
    ...data.map((point) => ({
      month: point.monthsFromNow,
      date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      percentile: point.percentile,
      status: point.status,
      remainingViolations: point.remainingViolations,
      violationsAgedOut: point.violationsAgedOut,
    })),
  ];

  // Custom tooltip component
  interface ChartDataPoint {
    date: string;
    percentile: number | null;
    status: string;
    remainingViolations: number | null;
    violationsAgedOut?: number;
    [key: string]: unknown;
  }

  const CustomTooltip: React.FC<TooltipProps<number, string>> = (props) => {
    const { active } = props;
    const payload = (props as unknown as { payload?: Array<{ payload: ChartDataPoint }> }).payload;
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border-2 border-gray-300 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900">{data.date}</p>
          <p className="text-sm text-gray-700 mt-1">
            Percentile: <span className="font-bold">{data.percentile !== null ? `${data.percentile}th` : 'N/A'}</span>
          </p>
          <p className="text-sm text-gray-700">
            Status: <span className={`font-semibold ${
              data.status === 'EXCELLENT' ? 'text-green-600' :
              data.status === 'SAFE' ? 'text-blue-600' :
              data.status === 'WARNING' ? 'text-yellow-600' : 'text-red-600'
            }`}>{data.status}</span>
          </p>
          {data.remainingViolations !== null && (
            <p className="text-sm text-gray-700">
              Violations: <span className="font-semibold">{data.remainingViolations}</span>
            </p>
          )}
          {data.violationsAgedOut !== undefined && data.violationsAgedOut > 0 && (
            <p className="text-sm text-green-600">
              Aged out: <span className="font-semibold">{data.violationsAgedOut}</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-4">
        {basicId}: {basicName} - Score Projection
      </h3>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          <XAxis
            dataKey="month"
            label={{ value: 'Months Ahead', position: 'insideBottom', offset: -5 }}
            tickFormatter={(value) => `${value}mo`}
          />

          <YAxis
            label={{ value: 'Percentile', angle: -90, position: 'insideLeft' }}
            domain={[0, 100]}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend />

          {/* Threshold line */}
          <ReferenceLine
            y={threshold}
            stroke="#ef4444"
            strokeDasharray="5 5"
            strokeWidth={2}
            label={{
              value: `Threshold (${threshold}%)`,
              position: 'right',
              fill: '#ef4444',
              fontSize: 12,
            }}
          />

          {/* Current score marker at month 0 */}
          <ReferenceLine
            x={0}
            stroke="#3b82f6"
            strokeDasharray="3 3"
            label={{
              value: 'Today',
              position: 'top',
              fill: '#3b82f6',
              fontSize: 12,
            }}
          />

          {/* Trajectory line */}
          <Line
            type="monotone"
            dataKey="percentile"
            stroke="#10b981"
            strokeWidth={3}
            dot={<Dot r={5} fill="#10b981" />}
            name="Projected Percentile"
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-4 text-sm text-gray-600 space-y-1">
        <p>* Projections assume no new violations are added</p>
        <p>* Scores improve as older violations age out (24-month window)</p>
        <p>* Hover over data points to see detailed information</p>
      </div>
    </div>
  );
};

export default TrajectoryChart;
